"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConsolidatedReportsTable from "../../../components/ConsolidatedReportsTable/ConsolidatedReportsTable";
import { getAllUnits } from "../../../redux/unitSlice/unitSlice";
import ActiveProject from "../../../assets/icons/Region.svg";
import { Select } from "antd";
import {
  getOperatingPersonalPayment,
  getOperatingMiscExpenses,
  getOperatingPerformance,
  getNotesToOperating,
} from "../../../redux/adminUnitReportSlice/adminUnitReportSlice";
import CustomTab from "../../../components/Common/CustomTab/CustomTab";
import CustomSelectWithFilter from "../../../components/Common/CustomSelect/CustomSelectWithFilter";

// const DEFAULT_FY = "2026-2027";
const getCurrentFY = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0 = Jan

  const fyStartYear = month >= 3 ? year : year - 1; // April-March FY

  return `${fyStartYear}-${fyStartYear + 1}`;
};

const DEFAULT_FY = getCurrentFY();

const tabData = [
  { label: "Operating Performance" },
  { label: "Notes to Operating" },
  { label: "Personnel Payments" },
  { label: "Miscellaneous Expenses" },
];

const getStartYearFromFY = (fy) => Number(fy?.split("-")[0]);

// ✅ Detect Percentage
const isPercentage = (val) => {
  if (val === null || val === undefined) return false;
  return val.toString().includes("%");
};

// ✅ Extract Numeric Value Safely
const getNumericValue = (val) => {
  if (!val) return 0;
  return Number(val.toString().replace(/,/g, "").replace("%", ""));
};

const OperatingPerformanceReport = () => {
  const dispatch = useDispatch();

  const { allUnitsList } = useSelector((state) => state.unit);
  const {
    operatingPerformance,
    operatingPersonalPayment,
    operatingMiscExpenses,
    notesToOperating,
    loading,
  } = useSelector((state) => state.unitReport);

  const [activeTab, setActiveTab] = useState("0");
  const [selectedFY, setSelectedFY] = useState(DEFAULT_FY);
  const [selectedUnitRegion, setSelectedUnitRegion] =
    useState("Select Unit/Region");
  const [selectedReportType, setSelectedReportType] = useState("For");

  const selectedFYStartYear = useMemo(
    () => getStartYearFromFY(selectedFY),
    [selectedFY]
  );

  /* ================= FETCH UNITS ================= */
  useEffect(() => {
    dispatch(getAllUnits());
  }, [dispatch]);

  /* ================= TAB BASED API CALL ================= */
  useEffect(() => {
    if (!selectedFYStartYear) return;

    const payload = {
      from_year: selectedFYStartYear,
      from_month: "04",
      to_year: selectedFYStartYear + 1,
      to_month: "03",
      report_type: selectedReportType,
      mnfUnit:
        selectedUnitRegion === "Select Unit/Region"
          ? ""
          : selectedUnitRegion,
    };

    switch (activeTab) {
      case "0":
        dispatch(getOperatingPerformance(payload));
        break;
      case "1":
        dispatch(getNotesToOperating(payload));
        break;
      case "2":
        dispatch(getOperatingPersonalPayment(payload));
        break;
      case "3":
        dispatch(getOperatingMiscExpenses(payload));
        break;
      default:
        break;
    }
  }, [
    dispatch,
    activeTab,
    selectedFYStartYear,
    selectedUnitRegion,
    selectedReportType,
  ]);

  /* ================= SELECT DATA BASED ON TAB ================= */
  const data = useMemo(() => {
    let selectedData = [];

    switch (activeTab) {
      case "0":
        selectedData = operatingPerformance || [];
        break;
      case "1":
        selectedData = notesToOperating || [];
        break;
      case "2":
        selectedData = operatingPersonalPayment || [];
        break;
      case "3":
        selectedData = operatingMiscExpenses || [];
        break;
      default:
        selectedData = [];
    }

    // ✅ COMMON FILTER HERE (ONLY CHANGE)
    const REMOVE_DESCRIPTIONS = new Set([
      "Value Added",
      "Value Added as a % of Revenue",
    ]);

    return selectedData.filter(
      (row) =>
        !REMOVE_DESCRIPTIONS.has(String(row?.DESCRIPTION || "").trim())
    );
  }, [
    activeTab,
    operatingPerformance,
    notesToOperating,
    operatingPersonalPayment,
    operatingMiscExpenses,
  ]);

  /* ================= COLUMNS ================= */
  const columns = useMemo(() => {
    if (!data?.length) return [];

    // ✅ pick first NON-heading row for keys (safer)
    const sampleRow =
      data.find((r) => String(r?.isHeading) !== "1") || data[0];

    const monthKeys = Object.keys(sampleRow || {}).filter((key) => {
      return (
        key.includes("_") &&
        !key.includes("formatted") &&
        key !== "HEAD_ID" &&
        key !== "ACTUAL_LAST_YEAR" &&
        key !== "DESCRIPTION" &&
        key !== "isHeading" &&
        key !== "key"
      );
    });

    const baseColumns = [
      {
        title: "Description",
        dataIndex: "DESCRIPTION",
        key: "DESCRIPTION",
        fixed: "left",
        width: 250,
      },

      ...monthKeys.map((key) => ({
        title: key.replace("_", "-"),
        dataIndex: key,
        key,
        align: "right",
        width: 120,
        render: (val, record) => {
          // ✅ heading row -> keep blank space
          if (String(record?.isHeading) === "1") return "\u00A0";

          if (isPercentage(val)) return val;

          const numericValue = getNumericValue(val);

          return numericValue.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      })),
    ];

    // ✅ Add Total only if NOT "Upto"
    if (selectedReportType !== "Upto") {
      baseColumns.push({
        title: "Total",
        key: "row_total",
        align: "right",
        width: 150,
        fixed: "right",
        render: (_, record) => {
          // ✅ heading row -> blank space
          if (String(record?.isHeading) === "1") return "\u00A0";

          const total = monthKeys.reduce((sum, key) => {
            const value = record[key];
            if (isPercentage(value)) return sum; // Skip %
            return sum + getNumericValue(value);
          }, 0);

          return total.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      });
   } else {
  baseColumns.push({
    title: "Total",
    key: "row_total",
    align: "right",
    width: 150,
    fixed: "right",
    render: (_, record) => {
      // ✅ heading row -> blank space
      if (String(record?.isHeading) === "1") return "\u00A0";

      if (!monthKeys.length) return "-";

      const lastKey = monthKeys[monthKeys.length - 1];
      const value = record[lastKey];

      if (isPercentage(value)) return value;

      const numericValue = getNumericValue(value);

      return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  });
}

    return baseColumns;
  }, [data, selectedReportType]);

  /* ================= TABLE DATA ================= */
  const tableData = useMemo(() => {
    if (!data.length) return [];

    return data.map((item, index) => ({
      ...item,
      key: index,
    }));
  }, [data]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="loader w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5">
      {/* ================= HEADER ================= */}
      <div className="flex gap-3 items-center mb-4">
        <img src={ActiveProject} alt="region" />
        <div>
          <div className="text-[24px] font-semibold">
            {sessionStorage.getItem("role") === "ADMIN"
              ? "BHEL"
              : JSON.parse(sessionStorage.getItem("user"))
                  ?.email.split("_")
                  .join(" ")}{" "}
            Performance Plan - Operating Report
          </div>
          <div className="text-gray-500 text-sm">
            * All figures are in Rupees (in lakhs)
          </div>
        </div>
      </div>

      {/* ================= TABS ================= */}
      <CustomTab
        tabItems={tabData}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* ================= FILTERS + TABLE ================= */}
      <div className="mt-6 bg-white shadow-lg rounded-xl p-4">
        <div className="flex justify-end gap-3 mb-4">
          <CustomSelectWithFilter
            showSearch
            data={
              Array.isArray(allUnitsList)
                ? allUnitsList.map((value) => ({
                    label: value?.UNIT_DISPLAY_NAME,
                    value: value?.MNF_UNIT,
                  }))
                : []
            }
            onChange={(value) => setSelectedUnitRegion(value)}
            value={selectedUnitRegion}
            placeholder="Select Unit/Region"
            className="w-[15rem]"
          />

          <Select
            className="w-[12rem]"
            value={selectedFY}
            onChange={setSelectedFY}
            options={[
              { label: "FY 2023-24", value: "2023-2024" },
              { label: "FY 2024-25", value: "2024-2025" },
              { label: "FY 2025-26", value: "2025-2026" },
              { label: "FY 2026-27", value: "2026-2027" },
              { label: "FY 2027-28", value: "2027-2028" },
              { label: "FY 2028-29", value: "2028-2029" },
              { label: "FY 2029-30", value: "2029-2030" },
            ]}
          />

          <Select
            className="w-[12rem]"
            value={selectedReportType}
            onChange={setSelectedReportType}
            options={[
              { label: "For", value: "For" },
              { label: "Upto", value: "Upto" },
            ]}
          />
        </div>

        <ConsolidatedReportsTable
          columns={columns}
          dataSource={tableData}
          scroll={{ x: "max-content", y: 800 }}
          sticky
        />
      </div>
    </div>
  );
};

export default OperatingPerformanceReport;